import { useQuery } from '@tanstack/react-query';
import {
  fetchMediaToken,
  buildSignedStreamUrl,
  buildSignedThumbnailUrl,
} from '@/services/videoApi';
import { fetchTeachingMediaToken, buildSignedTeachingStreamUrl } from '@/services/teachingVideoApi';
import {
  fetchCalibrationTakeMediaToken,
  buildSignedCalibrationTakeStreamUrl,
} from '@/services/templateApi';
import type { MediaTokenResponse } from '@/services/videoApi';

/**
 * Media-auth security sprint (2026-09-21) — see MEDIA_AUTH_CONTRACT.md.
 *
 * `/videos/{id}/stream` and `/videos/{id}/thumbnail` no longer allow anonymous
 * access; since a bare `<video src>` / `<img src>` never sends the bearer
 * header, these hooks fetch a short-lived signed token (`GET
 * /videos/{id}/media-token`) and build `sig`/`exp`-qualified URLs from it.
 *
 * The token is refreshed shortly before it expires (`expires_in - 60`
 * seconds) so a long-open page never has its `<video>`/`<img>` src go stale
 * mid-session. Consumers must treat `streamUrl`/`thumbnailUrl(...)` as
 * `null` while the token is loading — DO NOT fall back to the unsigned
 * `getVideoUrl`/`getThumbnailUrl` helpers, which will 401.
 */

/** Seconds of safety margin subtracted from `expires_in` before the token is
 *  considered due for refresh. Mirrors the backend's `MEDIA_URL_TTL_SECONDS`
 *  default (600s) margin called out in the contract. */
const REFRESH_MARGIN_SECONDS = 60;

function refreshMs(expiresIn: number | undefined): number {
  if (!expiresIn) return 0;
  return Math.max((expiresIn - REFRESH_MARGIN_SECONDS) * 1000, 0);
}

export interface UseMediaUrlResult {
  streamUrl: string | null;
  thumbnailUrl: (frame?: number, width?: number) => string | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Signed stream/thumbnail URLs for an athlete/template video.
 *
 * `videoId` may be undefined while the caller's own data is still loading —
 * the query is disabled and streamUrl/thumbnailUrl(...) resolve to null.
 */
export function useMediaUrl(videoId: string | undefined): UseMediaUrlResult {
  const query = useQuery<MediaTokenResponse>({
    queryKey: ['media-token', 'video', videoId],
    queryFn: () => fetchMediaToken(videoId!),
    enabled: !!videoId,
    staleTime: (q) => refreshMs(q.state.data?.expires_in),
    // Refetch just before the signed URL would expire so a long-open page
    // keeps a valid src. `false` (never) when we don't yet know expires_in.
    refetchInterval: (q) => refreshMs(q.state.data?.expires_in) || false,
  });

  const token = query.data;

  const streamUrl = videoId && token ? buildSignedStreamUrl(videoId, token) : null;
  const thumbnailUrl = (frame?: number, width?: number): string | null =>
    videoId && token ? buildSignedThumbnailUrl(videoId, token, frame, width) : null;

  return {
    streamUrl,
    thumbnailUrl,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export interface UseTeachingMediaUrlResult {
  streamUrl: string | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Signed stream URL for a teaching video/audio file (kind="teaching").
 * No thumbnail endpoint exists for teaching videos.
 */
export function useTeachingMediaUrl(id: string | undefined): UseTeachingMediaUrlResult {
  const query = useQuery<MediaTokenResponse>({
    queryKey: ['media-token', 'teaching', id],
    queryFn: () => fetchTeachingMediaToken(id!),
    enabled: !!id,
    staleTime: (q) => refreshMs(q.state.data?.expires_in),
    refetchInterval: (q) => refreshMs(q.state.data?.expires_in) || false,
  });

  const token = query.data;
  const streamUrl = id && token ? buildSignedTeachingStreamUrl(id, token) : null;

  return {
    streamUrl,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export interface UseCalibrationTakeMediaUrlResult {
  streamUrl: string | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Signed stream URL for an Example Performance (a coach-exposed calibration
 * take, kind="calibration_take") — backlog/example-performances-template-
 * resources.md, PR 1+2. No thumbnail endpoint exists for calibration takes,
 * mirroring `useTeachingMediaUrl`.
 *
 * Both `templateId` and `takeId` may be undefined while the caller's own
 * list query is still loading — the token query is disabled and `streamUrl`
 * resolves to `null`. Consumers MUST treat `null` as "not ready yet" and
 * never fall back to an unsigned URL — the `/stream` route 401s without a
 * valid `sig`/`exp` pair or a bearer header, which a bare `<video src>`
 * never sends.
 */
export function useCalibrationTakeMediaUrl(
  templateId: string | undefined,
  takeId: string | undefined,
): UseCalibrationTakeMediaUrlResult {
  const query = useQuery<MediaTokenResponse>({
    queryKey: ['media-token', 'calibration_take', templateId, takeId],
    queryFn: () => fetchCalibrationTakeMediaToken(templateId!, takeId!),
    enabled: !!templateId && !!takeId,
    staleTime: (q) => refreshMs(q.state.data?.expires_in),
    refetchInterval: (q) => refreshMs(q.state.data?.expires_in) || false,
  });

  const token = query.data;
  const streamUrl =
    templateId && takeId && token
      ? buildSignedCalibrationTakeStreamUrl(templateId, takeId, token)
      : null;

  return {
    streamUrl,
    isLoading: query.isLoading,
    error: query.error,
  };
}
